import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, ShoppingCart, Heart, Laptop, 
  Hotel, Factory, Banknote, Briefcase, 
  CheckCircle, ArrowRight 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { industries } from '../utils/mockData';

import usePageSeo from '../hooks/usePageSeo';
const IndustryExpertise = () => {
  usePageSeo({
    title: 'Industry Expertise | Sectors We Serve in UAE',
    description: 'Specialised audit, tax and advisory services across UAE industries — real estate, retail, manufacturing, technology, financial services and more.',
    path: '/industries'
  });

  const iconMap = {
    Building2, ShoppingCart, Heart, Laptop,
    Hotel, Factory, Banknote, Briefcase
  };

  const benefits = [
    {
      title: 'Industry-Specific Knowledge',
      description: 'Deep understanding of sector-specific regulations, challenges, and opportunities'
    },
    {
      title: 'Tailored Solutions',
      description: 'Customized audit, tax, and advisory services addressing unique industry needs'
    },
    {
      title: 'Regulatory Compliance',
      description: 'Expert guidance on industry-specific compliance requirements and best practices'
    },
    {
      title: 'Strategic Insights',
      description: 'Data-driven recommendations to optimize operations and drive growth'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-50 via-white to-gray-50">
        <div className="container mx-auto text-center">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Industry Expertise</span>
          <h1 className="text-5xl font-bold text-gray-900 mt-4 mb-6">
            Specialized Solutions for Your Industry
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Leveraging deep sector knowledge to deliver tailored audit, tax, and advisory services that address your industry's unique challenges and opportunities.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry) => {
              const Icon = iconMap[industry.icon];
              return (
                <Card 
                  key={industry.id} 
                  className="border-2 hover:border-teal-500 transition-all hover:shadow-lg group cursor-pointer"
                >
                  <CardContent className="p-6">
                    <div className="bg-teal-100 w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                      <Icon className="text-teal-600 group-hover:text-white transition-colors" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                      {industry.name}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {industry.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Industry Focus Matters */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Industry Expertise Matters
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Sector-specific knowledge enables us to deliver more relevant, effective, and valuable services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-xl hover:shadow-lg transition-all">
                <div className="bg-teal-100 p-3 rounded-lg flex-shrink-0">
                  <CheckCircle className="text-teal-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Industry Sections */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Industry-Specific Services
          </h2>

          <div className="space-y-12 max-w-6xl mx-auto">
            {/* Real Estate & Construction */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-teal-100 p-3 rounded-lg">
                    <Building2 className="text-teal-600" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Real Estate & Construction</h3>
                </div>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Comprehensive financial services for developers, contractors, and property management firms navigating complex project accounting and regulatory requirements.
                </p>
                <ul className="space-y-2">
                  {['Project cost accounting', 'Revenue recognition', 'Contract compliance', 'Tax optimization'].map((item, i) => (
                    <li key={i} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={18} className="text-teal-600 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=500&fit=crop&q=75&fm=webp" loading="lazy"
                alt="Real Estate"
                className="rounded-xl shadow-lg w-full h-80 object-cover"
              />
            </div>

            {/* Technology & Startups */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <img
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=500&fit=crop&q=75&fm=webp" loading="lazy"
                alt="Technology"
                className="rounded-xl shadow-lg w-full h-80 object-cover order-2 lg:order-1"
              />
              <div className="order-1 lg:order-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Laptop className="text-blue-600" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Technology & Startups</h3>
                </div>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Specialized support for tech companies and startups, from company formation to scaling operations and securing funding.
                </p>
                <ul className="space-y-2">
                  {['Company setup & structure', 'Funding advisory', 'R&D tax incentives', 'Growth-stage planning'].map((item, i) => (
                    <li key={i} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={18} className="text-blue-600 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Retail & E-commerce */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <ShoppingCart className="text-green-600" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Retail & E-commerce</h3>
                </div>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Tailored solutions for retail businesses and online merchants, addressing inventory management, VAT compliance, and multi-channel operations.
                </p>
                <ul className="space-y-2">
                  {['VAT optimization', 'Inventory valuation', 'Multi-channel accounting', 'Sales analytics'].map((item, i) => (
                    <li key={i} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <img
                src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=500&fit=crop&q=75&fm=webp" loading="lazy"
                alt="Retail"
                className="rounded-xl shadow-lg w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Industry Support */}
      <section className="py-20 px-4 bg-gradient-to-br from-teal-600 to-teal-700 text-white" data-testid="industry-support-section">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Supporting Businesses Across Industries</h2>
            <p className="text-teal-100 text-lg mb-8">
              We work with businesses across different sectors, providing accounting, audit, tax and advisory support tailored to their needs.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { id: 'accounting', title: 'Accounting & Compliance', text: 'Clear financial records and practical compliance support.' },
                { id: 'tax', title: 'Tax Advisory', text: 'Guidance based on your business and applicable tax requirements.' },
                { id: 'business', title: 'Business Support', text: 'Support for informed decisions at different stages of growth.' }
              ].map((card) => (
                <div key={card.id} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-colors" data-testid={`industry-support-card-${card.id}`}>
                  <h3 className="text-xl font-bold mb-2">{card.title}</h3>
                  <p className="text-teal-100">{card.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Discuss Your Industry Needs
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Let our industry experts understand your specific challenges and craft solutions that drive real business value.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg group">
              Schedule Industry Consultation
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default IndustryExpertise;
